import { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { PageHeader, Panel } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Lock, Trash2 } from "lucide-react";

function SchemaEditor({ schema, onSaved }) {
  const [fields, setFields] = useState(schema.fields);
  const [docs, setDocs] = useState(schema.documents);
  const save = async () => {
    try {
      const { data } = await api.put(`/admin/registration/schemas/${schema.classification}`, {
        fields: fields.map((f) => ({ key: f.key, required: f.required })), documents: docs });
      toast.success("Registration rules saved"); onSaved(data);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const addDoc = () => setDocs([...docs, { key: `custom_doc_${docs.length + 1}`, label: "New document", description: "", required: false }]);
  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <Panel title="Field requirements" description="Locked fields are essential for data integrity and stay required." testId={`fields-editor-${schema.classification}`}>
        {fields.map((f, i) => (
          <div key={f.key} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0 text-sm">
            <span>{f.label} <span className="text-xs text-slate-400">· {f.section}{f.required_if ? " · conditional" : ""}</span></span>
            <div className="flex items-center gap-2">
              {f.locked && <Lock className="h-3.5 w-3.5 text-slate-400" />}
              <Switch checked={f.required} disabled={f.locked} data-testid={`field-required-${f.key}`}
                onCheckedChange={(v) => setFields(fields.map((x, j) => (j === i ? { ...x, required: v } : x)))} />
            </div>
          </div>
        ))}
      </Panel>
      <Panel title="Supporting document rules" actions={<Button size="sm" variant="outline" onClick={addDoc} data-testid="add-doc-rule">Add document</Button>}>
        {docs.map((d, i) => (
          <div key={i} className="flex gap-2 items-center py-2 border-b border-slate-100 last:border-0">
            <Input value={d.label} onChange={(e) => setDocs(docs.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))} data-testid={`doc-label-${i}`} />
            <div className="flex items-center gap-1 text-xs whitespace-nowrap"><Switch checked={d.required} onCheckedChange={(v) => setDocs(docs.map((x, j) => (j === i ? { ...x, required: v } : x)))} data-testid={`doc-required-${i}`} />Required</div>
            <Button size="icon" variant="ghost" onClick={() => setDocs(docs.filter((_, j) => j !== i))}><Trash2 className="h-4 w-4" /></Button>
          </div>
        ))}
        <Button className="mt-4" onClick={save} data-testid={`save-schema-${schema.classification}`}>Save {schema.label} rules</Button>
      </Panel>
    </div>
  );
}

export default function RegistrationConfig() {
  const [schemas, setSchemas] = useState([]);
  useEffect(() => { api.get("/registration/schemas").then((r) => setSchemas(r.data)); }, []);
  if (!schemas.length) return null;
  return (
    <div>
      <PageHeader eyebrow="System Settings" title="Registration setup" description="Configure field requirements and required documents per subscriber classification." />
      <Tabs defaultValue="residential">
        <TabsList>{schemas.map((s) => <TabsTrigger key={s.classification} value={s.classification} data-testid={`schema-tab-${s.classification}`}>{s.label} · v{s.version}</TabsTrigger>)}</TabsList>
        {schemas.map((s) => (
          <TabsContent key={s.classification} value={s.classification} className="mt-6">
            <SchemaEditor schema={s} onSaved={(n) => setSchemas(schemas.map((x) => (x.classification === n.classification ? n : x)))} />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
